const CHECK_IN_OPENS_BEFORE_MS = 15 * 60 * 1000;
// A timed game closes 12 hours after the start; a TBD game 12 hours after the
// end of its date, i.e. 12:00 UTC the day after. Same rule as the server.
const CHECK_IN_CLOSES_AFTER_MS = 12 * 60 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function pad2(n) {
  return n < 10 ? `0${n}` : String(n);
}

// The wall clock at instant `ms`: on the device, or in `timeZone` when given.
function wallClock(ms, timeZone) {
  const date = new Date(ms);
  if (!timeZone) {
    return {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate(),
      weekday: date.getDay(),
      hour: date.getHours(),
      minute: date.getMinutes(),
    };
  }
  const parts = {};
  new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  })
    .formatToParts(date)
    .forEach((part) => {
      parts[part.type] = parseInt(part.value, 10);
    });
  return {
    year: parts.year,
    month: parts.month,
    day: parts.day,
    weekday: new Date(Date.UTC(parts.year, parts.month - 1, parts.day)).getUTCDay(),
    hour: parts.hour % 24,
    minute: parts.minute,
  };
}

function clockText(hour, minute) {
  return `${hour % 12 || 12}:${pad2(minute)} ${hour >= 12 ? 'PM' : 'AM'}`;
}

function hasTime(event) {
  return !!String((event && event.time) || '').trim();
}

export function schoolIdOf(user) {
  const school = user && (user.active_school || user.activeSchool);
  if (!school || typeof school !== 'object') return '';
  return String(school.id || school.schoolId || school.school_id || '').trim();
}

export function pickSchoolLogoUrl(user) {
  const school = user && (user.active_school || user.activeSchool);
  if (!school || typeof school !== 'object') return '';
  return String(school.image || school.logo || '').trim();
}

export function pickSchoolDisplay(user) {
  const school = user && (user.active_school || user.activeSchool);
  if (!school || typeof school !== 'object') return '';
  const abbr = school.abbreviation != null ? school.abbreviation : school.abbr;
  if (abbr != null && String(abbr).trim()) return String(abbr).trim();
  return school.name ? String(school.name) : '';
}

export function normalizeMediaUrl(url) {
  if (!url || typeof url !== 'string') return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('s3://app.gethomecrowd.com/')) {
    return url.replace('s3://app.gethomecrowd.com/', 'https://app.gethomecrowd.com/');
  }
  if (url.startsWith('s3://')) return url.replace('s3://', 'https://');
  return `https://app.gethomecrowd.com/${url.replace(/^\/+/, '')}`;
}

// A timed game is an instant, printed in local time. A TBD game has only the
// day it was entered on, printed as is so it never shifts to a neighbour day.
export function formatEventDate(event, timeZone) {
  if (!event) return '';
  const startMs = hasTime(event) ? eventStartMs(event) : null;
  if (startMs != null) {
    const local = wallClock(startMs, timeZone);
    return `${MONTHS[local.month - 1]} ${local.day}, ${local.year}`;
  }
  const date = String(event.date || '');
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!match) return date;
  return `${MONTHS[Number(match[2]) - 1]} ${Number(match[3])}, ${match[1]}`;
}

export function formatEventTime(event, timeZone) {
  const startMs = hasTime(event) ? eventStartMs(event) : null;
  if (startMs == null) return 'TBD';
  const local = wallClock(startMs, timeZone);
  return clockText(local.hour, local.minute);
}

export function toScore(value) {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export function hasFinalScore(event) {
  return toScore(event && event.home_score) != null && toScore(event && event.away_score) != null;
}

export function isPastEvent(event, nowMs) {
  const closesMs = checkInClosesMs(event);
  if (closesMs == null) return false;
  const now = nowMs != null ? nowMs : Date.now();
  return closesMs <= now;
}

function parseIsoMs(value) {
  if (!value) return null;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

function wallClockMs(date, hour, minute, second) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
  const ms = Date.UTC(
    Number(date.slice(0, 4)),
    Number(date.slice(5, 7)) - 1,
    Number(date.slice(8, 10)),
    hour,
    minute,
    second
  );
  return Number.isFinite(ms) ? ms : null;
}

export function eventStartMs(event) {
  const iso = parseIsoMs(event && event.starts_at);
  if (iso != null) return iso;
  const date = String((event && event.date) || '').trim();
  const time = String((event && event.time) || '').trim();
  let hour = 0;
  let minute = 0;
  let second = 0;
  if (time) {
    const parts = time.split(':');
    hour = parseInt(parts[0], 10);
    minute = parseInt(parts[1] || '0', 10);
    second = parseInt(parts[2] || '0', 10);
    if (Number.isNaN(hour) || Number.isNaN(minute) || Number.isNaN(second)) {
      hour = 0;
      minute = 0;
      second = 0;
    }
  }
  return wallClockMs(date, hour, minute, second);
}

export function checkInOpensMs(event) {
  const iso = parseIsoMs(event && event.check_in_opens_at);
  if (iso != null) return iso;
  const startMs = eventStartMs(event);
  return startMs == null ? null : startMs - CHECK_IN_OPENS_BEFORE_MS;
}

export function checkInClosesMs(event) {
  const iso = parseIsoMs(event && event.check_in_closes_at);
  if (iso != null) return iso;
  const startMs = eventStartMs(event);
  if (startMs == null) return null;
  return startMs + (hasTime(event) ? 0 : DAY_MS) + CHECK_IN_CLOSES_AFTER_MS;
}

export function isWithinCheckInWindow(event, nowMs) {
  const opensMs = checkInOpensMs(event);
  const closesMs = checkInClosesMs(event);
  if (opensMs == null || closesMs == null) return false;
  const now = nowMs != null ? nowMs : Date.now();
  return now >= opensMs && now < closesMs;
}

export function predictionLockMs(event) {
  const iso = parseIsoMs(event && event.prediction_locks_at);
  if (iso != null) return iso;
  const date = String((event && event.date) || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
  if (!String((event && event.time) || '').trim()) {
    return wallClockMs(date, 23, 59, 59);
  }
  return eventStartMs(event);
}

export function isPredictionOpen(event, nowMs) {
  const lockMs = predictionLockMs(event);
  if (lockMs == null) return false;
  const now = nowMs != null ? nowMs : Date.now();
  return now < lockMs;
}

export function canCheckIn(event, nowMs) {
  return !!(
    event &&
    event.check_in_enabled &&
    !event.already_checked_in &&
    isWithinCheckInWindow(event, nowMs)
  );
}

// The web page prints the window on a 24-hour clock ("Sat, Oct 17, 18:45").
export function formatWindowTime(ms, timeZone) {
  if (ms == null || !Number.isFinite(ms)) return '';
  const local = wallClock(ms, timeZone);
  const day = `${WEEKDAYS[local.weekday]}, ${MONTHS[local.month - 1]} ${local.day}`;
  // A 12-hour clock, as the embed prints the window: "Sun, Oct 19, 7:30 PM".
  const hour12 = local.hour % 12 || 12;
  const meridiem = local.hour < 12 ? 'AM' : 'PM';
  return `${day}, ${hour12}:${pad2(local.minute)} ${meridiem}`;
}

export function readPrediction(event) {
  const pred = event && event.my_prediction;
  if (!pred || typeof pred !== 'object') return null;
  const home = Number(pred.home_score);
  const away = Number(pred.away_score);
  if (!Number.isFinite(home) || !Number.isFinite(away)) return null;
  return { home, away };
}

export function predictionLabel(event) {
  const scores = readPrediction(event);
  if (!scores) return '';
  return `${scores.home}:${scores.away}`;
}

export function flattenScheduleEvents(schedules, eventsBySchedule, user) {
  const homeName = pickSchoolDisplay(user);
  const homeLogo = pickSchoolLogoUrl(user);
  const rows = [];
  (schedules || []).forEach((sport) => {
    const list = eventsBySchedule[sport.id] || [];
    list.forEach((event) => {
      rows.push({
        id: event.id,
        date: event.date || '',
        time: event.time || '',
        location_name: event.location_name || '',
        is_home: event.is_home !== false,
        latitude: Number.isFinite(Number(event.latitude)) ? Number(event.latitude) : null,
        longitude: Number.isFinite(Number(event.longitude)) ? Number(event.longitude) : null,
        check_in_radius_m: Number.isFinite(Number(event.check_in_radius_m))
          ? Number(event.check_in_radius_m)
          : 150,
        check_in_enabled: !!event.check_in_enabled,
        check_in_points: Number.isFinite(Number(event.check_in_points))
          ? Number(event.check_in_points)
          : 500,
        already_checked_in: !!event.already_checked_in,
        already_predicted: !!event.already_predicted,
        my_prediction: event.my_prediction || null,
        home_score: toScore(event.home_score),
        away_score: toScore(event.away_score),
        sport_id: sport.id,
        sport: sport.sport || sport.name || '',
        opponent_name: event.opponent_name || '',
        opponent_logo: event.logo_url || event.opponent_logo_url || '',
        home_name: homeName,
        home_logo: homeLogo,
        starts_at: event.starts_at || '',
        prediction_locks_at: event.prediction_locks_at || '',
        check_in_opens_at: event.check_in_opens_at || '',
        check_in_closes_at: event.check_in_closes_at || '',
      });
    });
  });
  rows.sort((a, b) => {
    const dateCmp = String(a.date).localeCompare(String(b.date));
    if (dateCmp !== 0) return dateCmp;
    return String(a.time || '99').localeCompare(String(b.time || '99'));
  });
  return rows;
}

export function apiErrorMessage(error, fallback) {
  const data = error?.response?.data;
  if (typeof data === 'string' && data.trim()) return data;
  if (data && typeof data === 'object') {
    if (typeof data.error === 'string' && data.error.trim()) return data.error;
    if (typeof data.detail === 'string' && data.detail.trim()) return data.detail;
    if (typeof data.message === 'string' && data.message.trim()) return data.message;
    const values = Object.values(data);
    for (let i = 0; i < values.length; i += 1) {
      const value = values[i];
      if (Array.isArray(value) && value[0]) return String(value[0]);
      if (typeof value === 'string' && value.trim()) return value;
    }
  }
  const message = error?.message ? String(error.message) : '';
  if (message && !message.startsWith('Request failed')) return message;
  return fallback;
}
